import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import FlySafeForecast from './FlySafeForecast';
import MapWithFlySafe from './MapWithFlySafe';

const queryClient = new QueryClient();

const MarkdownFromFile: React.FC = () => {
  const [text, setText] = React.useState(`loading`);
  const reactRootsRef = React.useRef<Root[]>([]);

  const updateText = (newText: string): void => {
    for (const root of reactRootsRef.current) {
      root.unmount();
    }
    reactRootsRef.current = [];

    setText(newText);
  };

  React.useEffect(() => {
    const elements = document.getElementsByClassName('react-component');
    for (const element of elements) {
      let attributes = {};
      try {
        attributes = JSON.parse(element.getAttribute('props') as string);
      } catch (e) {
        console.error(
          'Invalid json props given:',
          element.getAttribute('props'),
          e,
        );
      }
      const component = element.getAttribute('component');
      const root = createRoot(element);
      switch (component) {
        case 'MapWithFlySafe':
          root.render(
            <QueryClientProvider client={queryClient}>
              <MapWithFlySafe {...attributes} />
            </QueryClientProvider>,
          );
          reactRootsRef.current.push(root);
          break;
        case 'FlySafeForecast':
          root.render(
            <QueryClientProvider client={queryClient}>
              <FlySafeForecast {...attributes} />
            </QueryClientProvider>,
          );
          reactRootsRef.current.push(root);
          break;
        default:
          element.innerHTML = `<div class='error'>Error: unknown component ${component}<div>`;
      }
    }
  }, [text]);

  const updateContent = React.useCallback((): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleNotFound = (err: any): void => {
      console.error(err);
      setText('Not found');
      throw err;
    };

    const hash = window.location.hash.replace('#', '');
    let newURL = 'pages/index.md';
    if (hash === '/observations') {
      newURL = 'pages/observations.md';
    } else if (hash === '/forecast') {
      newURL = 'pages/forecast.md';
    } else if (hash !== '') {
      handleNotFound('Not found');
    }

    newURL = newURL.replace('//', '/');

    const suffix = newURL
      .split('/')
      .reverse()[0]
      .split('.')
      .slice(0, -1)
      .join('.');

    document.title =
      suffix === 'index'
        ? 'FlySafe Bird Avoidance Model Service - Homepage'
        : `FlySafe - ${suffix}`;

    fetch(newURL)
      .then(async (response) =>
        response.ok ? response.text() : Promise.reject(new Error('-')),
      )
      .then((result) => updateText(result))
      .catch(handleNotFound);
  }, []);

  React.useEffect(() => {
    window.addEventListener('hashchange', updateContent);
    updateContent();
    return (): void => {
      window.removeEventListener('hashchange', updateContent);
    };
  }, [updateContent]);

  return typeof text === 'string' ? (
    <ReactMarkdown rehypePlugins={[rehypeRaw]}>{text}</ReactMarkdown>
  ) : (
    text
  );
};
export default MarkdownFromFile;
